import React, { useRef, useEffect } from "react";
import { composeText, GUIDES } from "@/data/curriculumGeometry";
import { useCursiveFont } from "@/lib/cursiveFont";
import { LetterAnimator } from "@/components/tracing/LetterAnimator";
import { LetterGuide } from "@/components/tracing/LetterGuide";

// Renders the tracing widget for each letter, with the "Show me" animation
// auto-playing. Confirms the pen tip travels ALONG the letter, not next to it.
// Route: /_dev/cursive-trace-check
export default function CursiveTraceCheck() {
  const font = useCursiveFont();
  const letters = ["L", "A", "B", "Q", "S", "l", "b", "a"];
  return (
    <div className="min-h-screen bg-[#FDFBF6] p-6" data-testid="trace-check">
      <h1 className="mb-2 font-heading text-2xl font-extrabold text-[#1F2937]">Trace alignment check</h1>
      <p className="mb-4 font-body text-sm text-[#6B7280]">
        {font ? "Font loaded — orange pen should trace along the teal letter." : "Loading font…"}
      </p>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {letters.map((ch) => <TraceOne key={ch} ch={ch} />)}
      </div>
    </div>
  );
}

function TraceOne({ ch }) {
  const font = useCursiveFont();
  // The font is a hook dependency on purpose — when Cedarville Cursive
  // finishes loading, we want composeText to re-run so it returns font-derived
  // paths instead of the hand-coded fallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const traceable = React.useMemo(() => composeText(ch), [ch, font]);
  const animRef = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => animRef.current && animRef.current.replay(), 500);
    return () => clearTimeout(t);
  }, [font]);
  return (
    <div className="rounded-2xl border-2 border-[#E5DECF] bg-white p-3">
      <svg viewBox={traceable.viewBox} style={{ width: "100%", height: 180 }}>
        <LetterGuide letter={traceable} showLetter guideOpacity={0.35} />
        <LetterAnimator ref={animRef} letter={traceable} />
      </svg>
      <div className="mt-1 text-center font-mono text-xs text-[#6B7280]">{ch}</div>
    </div>
  );
}
